import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';

import { FormInput, FormPanel } from 'components/FormPanel';

import Button from 'primitives/Button';
import { Dialog, DialogPanel, DialogTitle } from 'primitives/Dialog';

const CREATE_FUND_INVITE_LINK_MUTATION = gql(`
  mutation CreateFundInviteLink(
    $fundId: ID!
    $minimumInvestmentAmount: Int
    $maximumInvestmentAmount: Int
    $note: String
  ) {
    createFundInviteLink(
      fundId: $fundId
      minimumInvestmentAmount: $minimumInvestmentAmount
      maximumInvestmentAmount: $maximumInvestmentAmount
      note: $note
    ) {
      id
      minimumInvestmentAmount
      maximumInvestmentAmount
      note
      status
      createdAt
    }
  }
`);

const CreateFundInviteLinkDialogButton: FC<{ fundId: string }> = ({ fundId }) => {
  const [open, setOpen] = useState(false);

  const [createFundInviteLink, { loading, error }] = useMutation(CREATE_FUND_INVITE_LINK_MUTATION, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        id: cache.identify({
          __typename: 'FundType',
          id: fundId,
        }),
        fields: {
          inviteLinks(existingInviteLinksRef) {
            const newInviteLinkNodeRef = cache.writeFragment({
              data: data.createFundInviteLink,
              fragment: gql(`
                fragment NewFundInviteLink on FundInviteLinkType {
                  id
                  status
                  minimumInvestmentAmount
                  maximumInvestmentAmount
                  note
                  createdAt
                }
              `),
            });
            const updatedInviteLinksRef = [newInviteLinkNodeRef, ...existingInviteLinksRef];
            return updatedInviteLinksRef;
          },
        },
      });
    },
  });

  return (
    <>
      <Button onClick={() => setOpen(true)}>Create Invite Link</Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogPanel>
          <DialogTitle>Create Invite Link</DialogTitle>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => setOpen(false)}
            onSubmit={data =>
              createFundInviteLink({
                variables: {
                  fundId,
                  minimumInvestmentAmount: data.setMinimumInvestmentAmount
                    ? data.minimumInvestmentAmount
                    : undefined,
                  maximumInvestmentAmount: data.setMaximumInvestmentAmount
                    ? data.maximumInvestmentAmount
                    : undefined,
                  note: data.note,
                },
              }).then(() => setOpen(false))
            }
          >
            <FormInput
              type="switch"
              fieldName="setMinimumInvestmentAmount"
              label="Set Minimum Investment Amount"
              defaultValue={false}
            />
            <FormInput
              type="currency"
              fieldName="minimumInvestmentAmount"
              label="Minimum Investment Amount"
              defaultValue={300000}
              conditionsToShow={{
                matches: [
                  {
                    field: 'setMinimumInvestmentAmount',
                    condition: '===',
                    value: 'true',
                  },
                ],
              }}
            />
            <FormInput
              type="switch"
              fieldName="setMaximumInvestmentAmount"
              label="Set Maximum Investment Amount"
              defaultValue={false}
            />
            <FormInput
              type="currency"
              fieldName="maximumInvestmentAmount"
              label="Maximum Investment Amount"
              defaultValue={1000000}
              conditionsToShow={{
                matches: [
                  {
                    field: 'setMaximumInvestmentAmount',
                    condition: '===',
                    value: 'true',
                  },
                ],
              }}
            />
            <FormInput type="textarea" fieldName="note" label="Note" defaultValue={''} />
          </FormPanel>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default CreateFundInviteLinkDialogButton;
